import { filmFacts } from "@/mocks/film";

export default function Hero() {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const highlights = [
    { icon: "ri-book-open-line", label: "剧情脉络", value: "6 幕" },
    { icon: "ri-movie-2-line", label: "深度解说", value: "3 视角" },
    { icon: "ri-film-line", label: "经典片段", value: "8 段" },
    { icon: "ri-magic-line", label: "彩蛋细节", value: "5 处" },
  ];

  return (
    <section id="top" className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20artistic%20Hong%20Kong%20cityscape%20at%20night%20seen%20from%20a%20high%20vantage%2C%20layered%20neon%20signs%20glowing%20in%20deep%20crimson%20red%20and%20warm%20amber%2C%20distant%20harbour%20reflections%2C%20cinematic%20film%20aesthetic%20with%20soft%20rain%20and%20haze%2C%20moody%20painterly%20texture%20and%20rich%20contrast&width=1920&height=1080&seq=hero-main-cover-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/50" />
      </div>

      <div className="relative w-full px-4 md:px-8 pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 font-label text-xs md:text-sm tracking-wider text-background-100 backdrop-blur">
              <i className="ri-movie-2-line text-accent-400" />
              香港电影 · 剧情延伸内容整理
            </span>
          </div>

          <h1
            className="mt-6 font-heading font-bold text-background-50 leading-[1.15] animate-fade-up text-4xl md:text-6xl lg:text-7xl"
            style={{ animationDelay: "120ms" }}
          >
            一路向西在线
          </h1>

          <p
            className="mt-5 font-heading text-xl md:text-3xl text-accent-300 leading-snug animate-fade-up"
            style={{ animationDelay: "220ms" }}
          >
            把剧情、解说、影评、片段、花絮与彩蛋，收在同一页里看明白
          </p>

          <p
            className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-background-200 animate-fade-up"
            style={{ animationDelay: "320ms" }}
          >
            本站面向想快速了解《一路向西》故事走向与看点的观众。整页按“先看懂故事、再看懂表达、最后回看细节”的顺序展开：
            从剧情脉络建立时间线，用深度解说与影评观点理解影片在讲什么，再用经典片段、幕后花絮和彩蛋细节补充观看体验。
            无需注册，所有内容均可直接阅读。
          </p>

          <div
            className="mt-9 flex flex-col sm:flex-row gap-3 animate-fade-up"
            style={{ animationDelay: "420ms" }}
          >
            <button
              type="button"
              onClick={() => scrollTo("plot")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 font-label text-sm md:text-base font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-route-line text-lg" />
              从剧情脉络读起
            </button>
            <button
              type="button"
              onClick={() => scrollTo("commentary")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-white/10 border border-white/25 text-background-50 font-label text-sm md:text-base font-medium hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap backdrop-blur"
            >
              <i className="ri-movie-2-line text-lg" />
              直接看深度解说
            </button>
          </div>

          <div
            className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 animate-fade-up"
            style={{ animationDelay: "520ms" }}
          >
            {highlights.map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-3 px-4 py-4 rounded-lg bg-white/10 border border-white/15 backdrop-blur"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/90 text-background-50 shrink-0">
                  <i className={`${item.icon} text-lg`} />
                </span>
                <span className="flex flex-col">
                  <span className="font-heading text-lg md:text-xl font-bold text-background-50">
                    {item.value}
                  </span>
                  <span className="font-label text-xs text-background-200">
                    {item.label}
                  </span>
                </span>
              </div>
            ))}
          </div>

          <div
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 animate-fade-up"
            style={{ animationDelay: "600ms" }}
          >
            {filmFacts.slice(0, 4).map((fact) => (
              <span
                key={fact.label}
                className="font-label text-xs md:text-sm text-background-200/90 whitespace-nowrap"
              >
                <span className="text-accent-300">{fact.label}</span>
                <span className="mx-2 text-background-400">/</span>
                {fact.value}
              </span>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="向下滚动到影片简介"
        onClick={() => scrollTo("intro")}
        className="absolute left-1/2 -translate-x-1/2 bottom-5 hidden md:flex flex-col items-center gap-1 text-background-200 hover:text-background-50 transition-colors cursor-pointer"
      >
        <span className="font-label text-[11px] tracking-widest">向下浏览</span>
        <i className="ri-arrow-down-line text-xl animate-float-soft" />
      </button>
    </section>
  );
}